import { getAuthToken, getCurrentUser } from "../services/authApi";

export const WALLET_SIGN_IN_HINT = "Sign with your wallet to continue.";
export const WALLET_SIGN_IN_SUBHINT =
  "Your wallet login creates dashboard access for your crypto purchases.";
export const WALLET_LINK_HINT = "Sign with your wallet to link it to your Treydora session.";

/**
 * @param {unknown} error
 */
export function formatWalletSessionError(error) {
  const msg =
    error?.shortMessage ||
    error?.reason ||
    error?.message ||
    (typeof error === "string" ? error : "");
  const s = String(msg);
  if (/rejected|denied|user rejected/i.test(s)) {
    return "Signature cancelled. You can retry when ready.";
  }
  if (/already linked to another/i.test(s)) {
    return "This wallet is already linked to another Treydora account.";
  }
  if (/no active sign-in challenge/i.test(s)) {
    return "Sign-in expired. Press Mint with Crypto Wallet again to get a new message.";
  }
  return s || "Wallet sign-in failed.";
}

function isWalletLinkedOnUser(user, walletAddress) {
  const lower = String(walletAddress || "").trim().toLowerCase();
  if (!lower) return false;
  return (user?.wallets || []).some(
    (w) =>
      w &&
      (!w.chain || w.chain === "ethereum") &&
      String(w.address || "").trim().toLowerCase() === lower
  );
}

/**
 * Ensures Bearer session + linked wallet for Oshar wallet mint / sync.
 * - No token → public wallet login (creates or loads wallet-only user).
 * - Token but wallet not on user → link flow (email/OAuth users).
 *
 * @param {{
 *   walletAddress: string,
 *   signMessageAsync: (args: { message: string }) => Promise<string>,
 *   walletLogin: (args: { walletAddress: string, signMessageAsync: Function }) => Promise<unknown>,
 *   linkWallet: (args: { walletAddress: string, signMessageAsync: Function }) => Promise<unknown>,
 * }} params
 * @returns {Promise<"wallet_login"|"wallet_link"|"ready">}
 */
export async function ensureTreydoraWalletSession({
  walletAddress,
  signMessageAsync,
  walletLogin,
  linkWallet,
}) {
  const addr = String(walletAddress || "").trim();
  if (!addr) {
    throw new Error("Wallet not connected.");
  }

  if (!getAuthToken()) {
    await walletLogin({ walletAddress: addr, signMessageAsync });
    return "wallet_login";
  }

  const user = getCurrentUser();
  if (!isWalletLinkedOnUser(user, addr)) {
    await linkWallet({ walletAddress: addr, signMessageAsync });
    return "wallet_link";
  }

  return "ready";
}
